import { useState, useEffect } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';

export default function TmsSystemParameters() {
  const [tab, setTab] = useState<'settings' | 'ph-rest' | 'weekly-ot'>('settings');

  const [settings, setSettings] = useState<any>({
    showResignedEmployees: false, resignedOnOrAfter: '', autoGenerateEmployeeCode: false,
    employeeCodePrefix: '', other1Label: 'Other 1', other2Label: 'Other 2', desigGroupLabel: 'Desig Group',
  });
  const [phMethod, setPhMethod] = useState(2);
  const [weeklyOt, setWeeklyOt] = useState<any>({
    isEnabled: false, firstDayOfWeek: 1, lastDayOfWeek: 7, eligibleWorkingHours: 44,
    otRate: 1.5, deductUnworked: false, includedWeekdays: [1, 2, 3, 4, 5, 6], includedReasonCodes: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const [s, ph, w] = await Promise.all([
      api.get('/tms/system-settings'),
      api.get('/tms/ph-rest-day-settings'),
      api.get('/tms/weekly-ot-settings'),
    ]);
    if (s.data.settings) {
      setSettings({
        showResignedEmployees: s.data.settings.show_resigned_employees,
        resignedOnOrAfter: s.data.settings.resigned_on_or_after?.slice(0, 10) || '',
        autoGenerateEmployeeCode: s.data.settings.auto_generate_employee_code,
        employeeCodePrefix: s.data.settings.employee_code_prefix || '',
        other1Label: s.data.settings.other_1_label,
        other2Label: s.data.settings.other_2_label,
        desigGroupLabel: s.data.settings.desig_group_label,
      });
    }
    if (ph.data.settings) setPhMethod(ph.data.settings.method);
    if (w.data.settings) {
      setWeeklyOt({
        isEnabled: w.data.settings.is_enabled,
        firstDayOfWeek: w.data.settings.first_day_of_week,
        lastDayOfWeek: w.data.settings.last_day_of_week,
        eligibleWorkingHours: w.data.settings.eligible_working_hours,
        otRate: w.data.settings.ot_rate,
        deductUnworked: w.data.settings.deduct_unworked,
        includedWeekdays: w.data.settings.included_weekdays || [],
        includedReasonCodes: w.data.settings.included_reason_codes || [],
      });
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/tms/system-settings', settings);
      alert('Settings saved');
    } finally {
      setSaving(false);
    }
  };

  const savePhMethod = async () => {
    setSaving(true);
    try {
      await api.put('/tms/ph-rest-day-settings', { method: phMethod });
      alert('PH/Rest Day method saved');
    } finally {
      setSaving(false);
    }
  };

  const saveWeeklyOt = async () => {
    setSaving(true);
    try {
      await api.put('/tms/weekly-ot-settings', weeklyOt);
      alert('Weekly OT settings saved');
    } finally {
      setSaving(false);
    }
  };

  const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">TMS System Parameters</h1>
      <div className="flex gap-2 mb-4 border-b">
        {(['settings', 'ph-rest', 'weekly-ot'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {t === 'settings' ? 'Settings' : t === 'ph-rest' ? 'PH-Rest Day Settings' : 'Weekly OT Setting'}
          </button>
        ))}
      </div>

      {tab === 'settings' && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4 max-w-2xl">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={settings.showResignedEmployees} onChange={(e) => setSettings({ ...settings, showResignedEmployees: e.target.checked })} />
            Show resigned employees
          </label>
          {settings.showResignedEmployees && (
            <Input label="On or after date" type="date" value={settings.resignedOnOrAfter} onChange={(e) => setSettings({ ...settings, resignedOnOrAfter: e.target.value })} />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={settings.autoGenerateEmployeeCode} onChange={(e) => setSettings({ ...settings, autoGenerateEmployeeCode: e.target.checked })} />
            Auto-generate employee code
          </label>
          {settings.autoGenerateEmployeeCode && (
            <Input label="Code prefix" value={settings.employeeCodePrefix} onChange={(e) => setSettings({ ...settings, employeeCodePrefix: e.target.value })} />
          )}
          <div className="grid grid-cols-3 gap-4">
            <Input label="Other 1 label" value={settings.other1Label} onChange={(e) => setSettings({ ...settings, other1Label: e.target.value })} />
            <Input label="Other 2 label" value={settings.other2Label} onChange={(e) => setSettings({ ...settings, other2Label: e.target.value })} />
            <Input label="Desig Group label" value={settings.desigGroupLabel} onChange={(e) => setSettings({ ...settings, desigGroupLabel: e.target.value })} />
          </div>
          <Button onClick={saveSettings} loading={saving}>Save</Button>
        </div>
      )}

      {tab === 'ph-rest' && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4 max-w-2xl">
          <p className="text-sm text-gray-600">Public Holiday / Rest Day pay method applied by the attendance engine when an employee works on a PH or rest day.</p>
          <Select
            label="Method"
            value={String(phMethod)}
            onChange={(e) => setPhMethod(Number(e.target.value))}
            options={[
              { value: '1', label: 'Method 1 (Old Rule) — pay actual hours worked' },
              { value: '2', label: 'Method 1 (New Rule) — <half day = half-day OT hours; >=half day = full-day OT hours' },
              { value: '3', label: 'Method 2 (MOM) — <half day = 1 day salary; >=half day = 2 days salary + OT beyond shift hours' },
            ]}
          />
          <Button onClick={savePhMethod} loading={saving}>Save</Button>
        </div>
      )}

      {tab === 'weekly-ot' && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4 max-w-2xl">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={weeklyOt.isEnabled} onChange={(e) => setWeeklyOt({ ...weeklyOt, isEnabled: e.target.checked })} />
            Enable Weekly OT
          </label>
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="First day of week"
              value={String(weeklyOt.firstDayOfWeek)}
              onChange={(e) => setWeeklyOt({ ...weeklyOt, firstDayOfWeek: Number(e.target.value) })}
              options={WEEKDAY_LABELS.map((l, i) => ({ value: String(i === 0 ? 7 : i), label: l }))}
            />
            <Select
              label="Last day of week"
              value={String(weeklyOt.lastDayOfWeek)}
              onChange={(e) => setWeeklyOt({ ...weeklyOt, lastDayOfWeek: Number(e.target.value) })}
              options={WEEKDAY_LABELS.map((l, i) => ({ value: String(i === 0 ? 7 : i), label: l }))}
            />
            <Input label="Eligible weekly hours" type="number" value={weeklyOt.eligibleWorkingHours} onChange={(e) => setWeeklyOt({ ...weeklyOt, eligibleWorkingHours: Number(e.target.value) })} />
            <Input label="OT rate for excess" type="number" step="0.1" value={weeklyOt.otRate} onChange={(e) => setWeeklyOt({ ...weeklyOt, otRate: Number(e.target.value) })} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={weeklyOt.deductUnworked} onChange={(e) => setWeeklyOt({ ...weeklyOt, deductUnworked: e.target.checked })} />
            Deduct unworked hours (if total hours &lt; eligible)
          </label>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Included weekdays</label>
            <div className="flex gap-3 flex-wrap">
              {WEEKDAY_LABELS.map((label, dow) => (
                <label key={dow} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={weeklyOt.includedWeekdays.includes(dow)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...weeklyOt.includedWeekdays, dow]
                        : weeklyOt.includedWeekdays.filter((d: number) => d !== dow);
                      setWeeklyOt({ ...weeklyOt, includedWeekdays: next });
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={saveWeeklyOt} loading={saving}>Save</Button>
        </div>
      )}
    </div>
  );
}
